import { useMemo, type ReactNode } from "react";
import { ArrowRight, Check, Film, RotateCcw } from "lucide-react";
import { cn } from "@/utils/helpers";
import type {
  AspectRatio,
  CalendarPlatform,
  IdeaReference,
} from "@/types/contentCalendar.type";
import { monthLabel } from "../calendar.utils";

/** Client hand-off after the team chooses one of three ideas. */
export type WindowPostState =
  | "waiting"
  | "approved"
  | "declined"
  | "reworking"
  | "missed"
  | "closed";

export interface WindowPost {
  id: string;
  scheduledAt: string;
  pillarName: string | null;
  format: string;
  title: string;
  synopsis: string | null;
  why: string | null;
  hook: string | null;
  reference: IdeaReference | null;
  channels: CalendarPlatform[];
  aspectRatio: AspectRatio;
  state: WindowPostState;
  answeredBy: string | null;
  reason: string | null;
  version?: number;
  versionCount?: number;
}

export type WindowBriefState = "waiting" | "approved" | "declined" | "revised" | "closed";

export interface WindowBrief {
  id: string;
  scheduledAt: string;
  kind: string;
  title: string;
  synopsis: string | null;
  state: WindowBriefState;
  answeredBy: string | null;
  version: number;
  /** False keeps production out of view until the concept gate is complete. */
  conceptApproved?: boolean;
}

/** Strip internal filing language from the rationale before it reaches a client. */
export function clientClaim(claim: string): string {
  const withoutFiling = claim.replace(/^Serves\s+[A-Za-z ]+?[.,]\s*/, "").trim();
  const [main, ...rest] = withoutFiling.split(/,\s*which\b/);
  const tail = rest.join(", which ");
  if (tail && /\bpillar\b/i.test(tail)) return main.replace(/[.,\s]+$/, "") + ".";
  return withoutFiling;
}

function stateCopy(post: WindowPost): string {
  switch (post.state) {
    case "waiting":
      return "Needs your answer";
    case "approved":
      return post.answeredBy ? `${post.answeredBy} approved this` : "Approved";
    case "declined":
      return "Changes requested";
    case "reworking":
      return "With the team";
    case "missed":
      return "Date passed";
    default:
      return "Read-only";
  }
}

function stateTone(state: WindowPostState | WindowBriefState): string {
  if (state === "waiting") return "border-co-accent text-co-accent-ink";
  if (state === "approved") return "border-[var(--co-live)] text-[var(--co-live)]";
  if (state === "declined" || state === "revised" || state === "reworking") {
    return "border-co-attention text-co-attention";
  }
  return "border-co-line2 text-co-faint";
}

function briefStateCopy(brief: WindowBrief): string {
  switch (brief.state) {
    case "waiting":
      return "Needs your answer";
    case "approved":
      return brief.answeredBy ? `${brief.answeredBy} approved this` : "Approved";
    case "declined":
      return "Changes requested";
    case "revised":
      return "New version ready";
    default:
      return "Read-only";
  }
}

function DateCell({ value }: { value: string }) {
  const date = new Date(value);
  return (
    <div className="flex min-h-full flex-col border-r border-co-line px-3 py-5 md:px-4">
      <span className="font-section text-[32px] font-extrabold tabular-nums leading-none text-co-text">
        {date.toLocaleDateString("en-GB", { day: "2-digit", timeZone: "UTC" })}
      </span>
      <span className="tt-label mt-1 uppercase text-co-faint">
        {date.toLocaleDateString("en-GB", { weekday: "short", timeZone: "UTC" })}
      </span>
    </div>
  );
}

function CollectionSection({
  title,
  count,
  description,
  children,
}: {
  title: string;
  count: number;
  description: string;
  children: ReactNode;
}) {
  return (
    <section>
      <header className="flex flex-col gap-2 border-b border-co-line2 pb-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex items-baseline gap-3">
          <h2 className="tt-title-m font-extrabold uppercase text-co-text">{title}</h2>
          <span className="tt-label tabular-nums uppercase text-co-faint">{count}</span>
        </div>
        <p className="tt-read-s max-w-[58ch] text-co-muted sm:text-right">{description}</p>
      </header>
      {children}
    </section>
  );
}

const rowButton =
  "tt-label inline-flex h-11 w-full items-center justify-center gap-2 whitespace-nowrap border border-co-line2 px-4 uppercase text-co-text transition-colors hover:border-co-text hover:bg-co-text hover:text-co-surface";

export default function ClientMonthView({
  brandName,
  year,
  month,
  signedBy,
  readerName,
  posts,
  briefs = [],
  onOpenPost,
  onOpenBrief,
  embedded = false,
  toolbar,
}: {
  /**
   * Inside the app (a Guest's calendar) rather than as its own page: no full
   * viewport height and no forced light palette, so it follows the cockpit.
   */
  embedded?: boolean;
  /** Rendered above the header — the guest's month switcher. */
  toolbar?: ReactNode;
  brandName: string;
  year: number;
  month: number;
  signedBy: string | null;
  readerName?: string | null;
  posts: WindowPost[];
  briefs?: WindowBrief[];
  reasonMin?: number;
  busyPostId?: string | null;
  decisionError?: { postId: string; message: string } | null;
  /** Kept for rollout compatibility; decisions now live inside concept review. */
  onApprove?: (postId: string) => void;
  /** Kept for rollout compatibility; decisions now live inside concept review. */
  onDecline?: (postId: string, reason: string) => void;
  onOpenPost?: (postId: string) => void;
  onOpenBrief?: (briefId: string) => void;
}) {
  const orderedPosts = useMemo(
    () => [...posts].sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)),
    [posts],
  );
  const visibleBriefs = useMemo(
    () =>
      briefs
        .filter((brief) => brief.conceptApproved === true)
        .sort((a, b) => a.scheduledAt.localeCompare(b.scheduledAt)),
    [briefs],
  );
  const waiting = orderedPosts.filter((post) => post.state === "waiting").length;
  const revising = orderedPosts.filter(
    (post) => post.state === "declined" || post.state === "reworking",
  ).length;
  const approved = orderedPosts.filter((post) => post.state === "approved").length;

  return (
    <div
      className={cn(
        "text-co-text",
        embedded ? "w-full" : "co-tokens-light min-h-dvh bg-co-ground px-4 py-8 sm:px-7 lg:px-12",
      )}
    >
      <main className="mx-auto flex w-full max-w-[92rem] flex-col gap-10">
        {toolbar}
        <header className="grid gap-5 border-b border-co-line2 pb-5 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-end">
          <div>
            <p className="tt-label uppercase text-co-accent-ink">
              {brandName} · {monthLabel({ year, month })}
            </p>
            <h1 className="tt-title-l mt-2 font-extrabold uppercase text-co-text">Content review</h1>
            <p className="tt-read-m mt-3 max-w-[68ch] leading-relaxed text-co-muted">
              Review each content idea with its reference and discussion. Production details appear as a separate Creative Brief after the concept is approved.
            </p>
          </div>
          <div className="grid grid-cols-3 border-y border-co-line2 sm:min-w-[28rem]">
            {[
              ["Needs answer", waiting, "text-co-accent-ink"],
              ["With team", revising, "text-co-attention"],
              ["Approved", approved, "text-[var(--co-live)]"],
            ].map(([label, count, tone], index) => (
              <div key={String(label)} className={cn("px-3 py-3", index > 0 && "border-l border-co-line")}>
                <span className="tt-label block uppercase text-co-faint">{label}</span>
                <span className={cn("font-section mt-2 block text-[28px] font-extrabold tabular-nums leading-none", tone)}>{count}</span>
              </div>
            ))}
          </div>
          <p className="tt-label lg:col-span-2 uppercase text-co-faint">
            Prepared by {signedBy ?? "Mohtawa"}{readerName ? ` · Viewing as ${readerName}` : ""}
          </p>
        </header>

        <CollectionSection
          title="Content ideas"
          count={orderedPosts.length}
          description="The concept, its reference and every shared note stay together across versions."
        >
          {orderedPosts.length > 0 ? (
            <ol className="border-b border-co-line2">
              {orderedPosts.map((post) => (
                <li
                  key={post.id}
                  className="grid grid-cols-[4rem_minmax(0,1fr)] border-t border-co-line2 bg-co-surface transition-colors hover:bg-co-surface-solid md:grid-cols-[5rem_minmax(0,1.5fr)_minmax(11rem,0.55fr)_14rem]"
                >
                  <DateCell value={post.scheduledAt} />
                  <div className="min-w-0 px-4 py-5 md:px-6">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className={cn("tt-label border px-2 py-1 uppercase", stateTone(post.state))}>{stateCopy(post)}</span>
                      <span className="tt-label uppercase text-co-faint">{post.pillarName ?? "Content plan"}</span>
                    </div>
                    <h3 className="tt-title-m truncate font-extrabold uppercase text-co-text">{post.title}</h3>
                    {post.synopsis ? <p className="tt-read-m mt-1 line-clamp-2 max-w-[66ch] leading-relaxed text-co-muted">{post.synopsis}</p> : null}
                  </div>
                  <div className="col-span-2 flex items-center gap-3 border-t border-co-line px-4 py-3 md:col-span-1 md:border-l md:border-t-0 md:px-5">
                    <span className="grid size-8 shrink-0 place-items-center border border-co-line2 text-co-faint"><Film className="size-4" strokeWidth={1.6} aria-hidden="true" /></span>
                    <div className="min-w-0">
                      <span className="tt-label block truncate uppercase text-co-faint">{post.format}</span>
                      <span className="tt-read-s text-co-text2">Version {post.version ?? 1}{post.versionCount && post.versionCount > 1 ? ` of ${post.versionCount}` : ""}</span>
                    </div>
                  </div>
                  <div className="col-span-2 flex items-center border-t border-co-line px-4 py-3 md:col-span-1 md:border-l md:border-t-0 md:px-5">
                    {onOpenPost ? (
                      <button type="button" onClick={() => onOpenPost(post.id)} className={rowButton}>
                        {post.state === "waiting" ? "Review content idea" : "Open content idea"}
                        <ArrowRight className="size-3.5" aria-hidden="true" />
                      </button>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <div className="border-b border-dashed border-co-line2 px-6 py-12 text-center">
              <p className="tt-read-m text-co-muted">No content ideas have been shared yet.</p>
            </div>
          )}
        </CollectionSection>

        <CollectionSection
          title="Creative briefs"
          count={visibleBriefs.length}
          description="Production details, deliverables and bilingual copy unlock only after concept approval."
        >
          {visibleBriefs.length > 0 ? (
            <ol className="border-b border-co-line2">
              {visibleBriefs.map((brief) => (
                <li key={brief.id} className="grid grid-cols-[4rem_minmax(0,1fr)] border-t border-co-line2 bg-co-surface md:grid-cols-[5rem_minmax(0,1.5fr)_minmax(11rem,0.55fr)_14rem]">
                  <DateCell value={brief.scheduledAt} />
                  <div className="min-w-0 px-4 py-5 md:px-6">
                    <div className="mb-2 flex flex-wrap items-center gap-2">
                      <span className={cn("tt-label border px-2 py-1 uppercase", stateTone(brief.state))}>{briefStateCopy(brief)}</span>
                      <span className="tt-label uppercase text-co-faint">Creative brief</span>
                    </div>
                    <h3 className="tt-title-m truncate font-extrabold uppercase text-co-text">{brief.title}</h3>
                    {brief.synopsis ? <p className="tt-read-m mt-1 line-clamp-2 max-w-[66ch] leading-relaxed text-co-muted">{brief.synopsis}</p> : null}
                  </div>
                  <div className="col-span-2 flex items-center gap-3 border-t border-co-line px-4 py-3 md:col-span-1 md:border-l md:border-t-0 md:px-5">
                    <span className="grid size-8 shrink-0 place-items-center border border-co-line2 text-co-faint">
                      {brief.state === "approved" ? <Check className="size-4" aria-hidden="true" /> : brief.state === "declined" || brief.state === "revised" ? <RotateCcw className="size-4" aria-hidden="true" /> : <Film className="size-4" aria-hidden="true" />}
                    </span>
                    <div><span className="tt-label block uppercase text-co-faint">{brief.kind}</span><span className="tt-read-s text-co-text2">Version {brief.version}</span></div>
                  </div>
                  <div className="col-span-2 flex items-center border-t border-co-line px-4 py-3 md:col-span-1 md:border-l md:border-t-0 md:px-5">
                    {onOpenBrief ? <button type="button" onClick={() => onOpenBrief(brief.id)} className={rowButton}>{brief.state === "waiting" ? "Review brief" : "Open brief"}<ArrowRight className="size-3.5" aria-hidden="true" /></button> : null}
                  </div>
                </li>
              ))}
            </ol>
          ) : (
            <div className="border-b border-dashed border-co-line2 px-6 py-10 text-center">
              <p className="tt-read-m text-co-muted">Approved concepts will unlock their Creative Briefs here.</p>
            </div>
          )}
        </CollectionSection>
      </main>
    </div>
  );
}
